import type { SettlingInPlanDetail } from '@/types/settlingIn';

/** The surface a sign-off was recorded through. `app` lands with #622. */
export type LifecycleChannel = 'admin' | 'app';

/**
 * One entry in the detail page's activity timeline.
 *
 * `labelId` is a stable dotted Lingui id rather than a rendered string, so this
 * module stays pure (no React, no i18n instance) and unit-testable.
 */
export interface LifecycleEvent {
  key: 'plan_created' | 'schedule_seeded' | 'staff_completion' | 'parent_sign_off';
  labelId: string;
  /** ISO timestamp or date string; undefined when the event has no recorded date. */
  timestamp?: string;
  /** Staff user id (plan_created / schedule_seeded / staff_completion). */
  actorId?: string;
  /** Contact id of the signing parent (parent_sign_off only). */
  contactId?: string;
  /** Present on parent_sign_off only, and only once migration 119 has data. */
  channel?: LifecycleChannel;
}

/** The subset of the plan detail this derivation reads. */
export type LifecycleEventsPlan = Pick<
  SettlingInPlanDetail,
  | 'created_at'
  | 'created_by'
  | 'end_date'
  | 'settling_in_completed'
  | 'sign_off_date'
  | 'sign_off_contact_id'
  | 'sign_off_recorded_by'
  | 'sign_off_channel'
  | 'completion_recorded_by'
  | 'schedule_entries'
>;

/** Narrow an arbitrary string to the known channel vocabulary. */
function toChannel(raw: string | undefined): LifecycleChannel | undefined {
  return raw === 'admin' || raw === 'app' ? raw : undefined;
}

/**
 * Reduce an event timestamp to a sortable epoch value.
 *
 * The timeline mixes two shapes: full ISO instants (`created_at`, sign-off) and
 * bare calendar dates (`end_date`). A bare date is anchored to UTC midnight so it
 * orders as the START of its day — which is what `Date.parse` does with the
 * date-only form anyway, but stating it here keeps the two shapes comparable on
 * one numeric axis instead of by accidental string ordering (lexicographic
 * comparison breaks the moment a timestamp carries a `+02:00` offset instead of
 * `Z`).
 *
 * Returns `null` for missing or unparseable values; the caller places those last
 * rather than letting `NaN` comparisons scatter them arbitrarily.
 */
function toSortKey(timestamp: string | undefined): number | null {
  if (!timestamp) return null;
  const parsed = Date.parse(timestamp);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Derive the lifecycle activity events for a plan, in chronological order.
 *
 * Events are omitted rather than rendered empty when their underlying fact does
 * not exist: no schedule entries means no `schedule_seeded` row, and an
 * incomplete plan has no `staff_completion` row. A timeline that lists events
 * that never happened is worse than a short one.
 */
export function buildLifecycleEvents(plan: LifecycleEventsPlan): LifecycleEvent[] {
  const events: LifecycleEvent[] = [];

  events.push({
    key: 'plan_created',
    labelId: 'settlingIn.timeline.event.planCreated',
    timestamp: plan.created_at,
    actorId: plan.created_by,
  });

  // A Go nil slice serialises to JSON `null`, so `?? []` is load-bearing:
  // without it the `.reduce` below throws and blanks the whole page.
  const scheduleEntries = plan.schedule_entries ?? [];
  const seededAt = scheduleEntries.reduce<string | undefined>((earliest, entry) => {
    const created = entry?.created_at;
    if (!created) return earliest;
    return !earliest || created < earliest ? created : earliest;
  }, undefined);
  if (seededAt) {
    events.push({
      key: 'schedule_seeded',
      labelId: 'settlingIn.timeline.event.scheduleSeeded',
      timestamp: seededAt,
      // The seeding actor is not separately recorded; the plan creator is the
      // best available attribution and is what the schedule rows carry.
      actorId: plan.created_by,
    });
  }

  if (plan.settling_in_completed === true) {
    events.push({
      key: 'staff_completion',
      labelId: 'settlingIn.timeline.event.staffCompletion',
      // `end_date` is the recorded completion date. The actor comes from the
      // dedicated `completion_recorded_by` column (migration 120) — NEVER from
      // `updated_by`, which every later write to the plan overwrites and which
      // therefore typically holds the SIGN-OFF actor rather than whoever
      // completed. Rows completed before migration 120 carry null here and render
      // date-only; showing a confidently wrong name is worse than showing none.
      timestamp: plan.end_date,
      actorId: plan.completion_recorded_by,
    });
  }

  if (plan.sign_off_date) {
    events.push({
      key: 'parent_sign_off',
      labelId: 'settlingIn.timeline.event.parentSignOff',
      timestamp: plan.sign_off_date,
      contactId: plan.sign_off_contact_id,
      actorId: plan.sign_off_recorded_by,
      // Rows signed off before migration 119 carry no channel — never defaulted
      // to 'admin', which would assert a fact the data does not support.
      channel: toChannel(plan.sign_off_channel),
    });
  }

  // Completion and sign-off are INDEPENDENT facts, so construction order is not
  // chronological order: a parent can sign off before staff record completion
  // (and routinely does, since sign-off is often what prompts completion). Sort
  // so the rendered timeline actually honours the contract documented above.
  //
  // Stable by construction: ties fall back to the original index, so equal
  // timestamps (a same-day completion + sign-off, both date-only) keep their
  // documented lifecycle order and tests stay deterministic. Events with no
  // usable timestamp cannot be placed on the axis at all, so they sink to the
  // end in their original relative order rather than sorting arbitrarily.
  return events
    .map((event, index) => ({ event, index, sortKey: toSortKey(event.timestamp) }))
    .sort((a, b) => {
      if (a.sortKey === null || b.sortKey === null) {
        if (a.sortKey === b.sortKey) return a.index - b.index;
        return a.sortKey === null ? 1 : -1;
      }
      if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
      return a.index - b.index;
    })
    .map(({ event }) => event);
}
